import { Controller } from '@hotwired/stimulus';
import { getNewThread } from '../../src/react/aiAssistant';
import {
  batchSetNodeKVPair,
  continueToNextSection,
  currentSection,
  dispatchCaseExperienceRefreshContinueButtonsIfTwoCol,
  getCsrfToken,
  getNextVisibleDocSection,
  updateDocumentUserState,
  waitForVisible,
  handleAiAssistantFallback,
  AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT,
  convertSimpleMarkdown,
  getDusFromDom,
  getDusEntryForUid,
  normalizeNodeUid,
  vpeChatErrorBlocksFeedback,
  showAiVpeFeedbackErrorModal,
  showAiErrorModal,
  registerErrorInRollbar,
} from '../../utils/case_experience/case_experience_in_rails';
import {
  closeRailsuiModal,
  openVpeFallbackErrorModal,
  removeDynamicVpeErrorModals,
  waitForEmbeddedVpeErrorModalReady,
  vpeErrorModalController,
} from '../../utils/case_experience/vpe_fallback_error_modal';
import generateUUID from '../../src/react/helpers/generateUUID';
import { railsStudentCaseUnlockContinue } from '../../utils/case_experience/case_experience_api';

/** No assistant text within this window -> show fatal error modal (matches React FEEDBACK_TIMEOUT_MS) */
const RESPONSE_TIMEOUT_MS = 120000;
/** After this quiet period post-content, treat empty/bad text as failure (matches React STREAMING_DONE_MS) */
const STREAMING_DONE_MS = 1500;
const FEEDBACK_BAD_PHRASE = 'We had trouble generating a response';
const HTTP_RETRY_MAX_ATTEMPTS = 3;
const HTTP_RETRY_DELAYS_MS = [400, 1200];
const feedbackGenerationInFlightCache = new Map();

/** In-memory cache for server-rendered Aquifer loading dots (one fetch per page load). */
let aquiferLoadingDotsHtml = null;
let aquiferLoadingDotsFetch = null;

async function fetchAquiferLoadingDotsHtml() {
  if (aquiferLoadingDotsHtml != null) return aquiferLoadingDotsHtml;
  if (!aquiferLoadingDotsFetch) {
    aquiferLoadingDotsFetch = (async () => {
      const res = await fetch('/aquifer_ui/library/components/loading_dots', {
        headers: {
          Accept: 'text/html',
          'X-CSRF-Token': getCsrfToken(),
        },
      });
      if (!res.ok) throw new Error(`Loading dots request failed: ${res.status}`);
      const html = await res.text();
      aquiferLoadingDotsHtml = html;
      return html;
    })().catch((error) => {
      aquiferLoadingDotsFetch = null;
      throw error;
    });
  }
  return await aquiferLoadingDotsFetch;
}

/**
 * Rails VPE feedback card: loads AI summary after the student finishes the chat card (chatFeedbackSubmitted).
 *
 * UI / state (for debugging):
 * - _feedbackStreamActive: true while we expect turbo-streamed assistant content in thread_messages_*.
 * - isLoading / isError: reflect response container + continue button; isError set on fatal paths.
 * - errorModalOpen: VPE feedback error overlay visible; cleared in hideErrorModal / disconnect.
 * - Flow: initializeFeedback -> maybe fetchAndMaybeGenerateFeedback -> _generateFeedback -> mirror observer
 *   copies assistant DOM into responseContainer; _persistFeedbackFromThread saves DUS feedback slice.
 * - vpe-chat-error / vpeChatErrorBlocksFeedback: chat card failed first; we show unavailable copy, no generation.
 * - vpeStructuredFeedback (version structured_output_type vpe_feedback_history or vpe_feedback_communication): structured partial + message id inside feedback.value;
 *   when false, markdown in message.content only.
 */
export default class extends Controller {
  static targets = [
    'responseContainer',
    'continueButton',
    'feedbackModal',
    'feedbackModalHost',
    'turboMount',
    'errorModal',
    'loadingTemplate',
  ];
  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    assistantId: Number,
    chatUid: String,
    threadId: String,
    inactive: Boolean,
    feedbackSubmitted: Boolean,
    chatFeedbackSubmitted: Boolean,
    vpeStructuredFeedback: Boolean,
  };

  /**
   * Reset runtime flags, subscribe to document events, defer initializeFeedback until section is visible.
   * (responseTimeout / timers / observer refs start null; chat thread mirroring attaches during generation.)
   */
  connect() {
    this.responseTimeout = null;
    this._feedbackCompletionTimer = null;
    this._latestFeedbackContent = '';
    this.isLoading = false;
    this.isError = false;
    this.userSectionStateValue = getDusFromDom();
    this._feedbackStreamActive = false;
    this._threadMessagesObserver = null;
    this._mirrorDebounceTimer = null;
    this.errorModalOpen = false;
    this._vpeStructuredFeedbackRendered = false;

    this._onVpeChatError = this._handleVpeChatError.bind(this);
    document.addEventListener('vpe-chat-error', this._onVpeChatError);

    this._onVpeFeedbackCardMayGenerate = this._handleVpeFeedbackCardMayGenerate.bind(this);
    document.addEventListener('vpe-feedback-card-may-generate', this._onVpeFeedbackCardMayGenerate);

    waitForVisible(currentSection(this.element), () => this.initializeFeedback());
  }

  /**
   * Entry: if DUS already has feedback, show it; else placeholder until chat submitted; else start fetch/generate.
   */
  initializeFeedback() {
    const dus = getDusFromDom();
    this.userSectionStateValue = dus;
    if (vpeChatErrorBlocksFeedback(dus)) {
      this.setChatErrorFeedbackMessage();
      return;
    }

    const feedback_array = getDusEntryForUid(dus, this.nodeUidValue)?.feedback?.value;
    if (Array.isArray(feedback_array) && this._feedbackArrayHasPersistedAssistant(feedback_array)) {
      this.displayFeedback(feedback_array);
      return;
    }
    const existingThreadId = this._resolvedThreadIdForVpeFeedbackSubmit();
    if (existingThreadId) {
      this._activeFeedbackThreadId = String(existingThreadId);
      const cacheKey = this._feedbackGenerationCacheKey(existingThreadId);
      if (cacheKey && feedbackGenerationInFlightCache.get(cacheKey) === true) {
        this._feedbackStreamActive = true;
        this.showLoading();
        void this
          ._ensureTurboMount(String(existingThreadId))
          .then(() => this._connectThreadMessagesObserver(String(existingThreadId)))
          .catch(() => {});
        return;
      }
    }
    this.setFeedbackPlaceholder();
    if (!this.chatFeedbackSubmittedValue) {
      return;
    }
    void this.fetchAndMaybeGenerateFeedback();
  }

  /** Chat card logged aiAssistantChatError: block generation and show static unavailable message */
  setChatErrorFeedbackMessage() {
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
    }
    this.isLoading = false;
    this._feedbackStreamActive = false;
    this._disconnectThreadMirror();
    const responseContainer = this.responseContainerTarget;
    const continueButton = this.continueButtonTarget;
    if (responseContainer) {
      responseContainer.innerHTML =
        '<p class="ai-feedback-unavailable">Feedback is unavailable because the Virtual Patient Encounter encountered an error. Use the error dialog to continue.</p>';
    }
    if (continueButton) {
      continueButton.disabled = true;
    }

    void showAiErrorModal({
      documentId: this.documentIdValue,
      application: this.application,
      onContinue: () => this._enableVpeChatAiFallbackMode(),
      buttonId: 'ai-assistant-feedback-chat-error-modal-continue-button',
      modalId: 'ai-assistant-feedback-chat-error-modal',
      targetController: 'ai-assistant-feedback',
      fallbackModalRoot: this.hasErrorModalTarget ? this.errorModalTarget : null,
    }).then((shown) => {
      this.errorModalOpen = shown;
    });
  }

  async _enableVpeChatAiFallbackMode() {
    const response = await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: {
        document_user_state: {
          state: {
            root: {
              error: { value: 'aiAssistantChatError', updatedAt: Date.now() },
              aiFallbackCards: { value: true, updatedAt: Date.now() },
            },
          },
        },
      },
    });
    if (!response) return;

    await handleAiAssistantFallback({
      currentSectionElement: currentSection(this.element),
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      application: this.application,
      onAfterFallback: () => {
        removeDynamicVpeErrorModals();
      },
    });
  }

  // Dispatched when VPE chat hits fatal error (DUS root error)
  _handleVpeChatError() {
    this.setChatErrorFeedbackMessage();
  }

  /**
   * Fired from chat card after “no more questions” flow: flip chatFeedbackSubmitted and start generation
   * if we do not already have persisted feedback.
   */
  _handleVpeFeedbackCardMayGenerate = (e) => {
    const { documentId, chatUid } = e.detail || {};
    if (documentId == null || chatUid == null) return;
    if (Number(documentId) !== Number(this.documentIdValue)) return;
    if (normalizeNodeUid(this.chatUidValue) !== normalizeNodeUid(chatUid)) return;
    if (this.feedbackSubmittedValue) return;
    const dus = getDusFromDom();
    this.userSectionStateValue = dus;
    if (vpeChatErrorBlocksFeedback(dus)) {
      this.setChatErrorFeedbackMessage();
      return;
    }
    this.chatFeedbackSubmittedValue = true;
    const feedbackArr = getDusEntryForUid(dus, this.nodeUidValue)?.feedback?.value;
    if (Array.isArray(feedbackArr) && this._feedbackArrayHasPersistedAssistant(feedbackArr)) return;
    if (this._feedbackFetchInFlight) return;
    if (this._feedbackStreamActive) return;
    void this.fetchAndMaybeGenerateFeedback();
  };

  /** Tear down modal, listeners, mirror observer, and timers */
  disconnect() {
    this.hideErrorModal();
    document.removeEventListener('vpe-chat-error', this._onVpeChatError);
    document.removeEventListener('vpe-feedback-card-may-generate', this._onVpeFeedbackCardMayGenerate);
    this._disconnectThreadMirror();
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
    }
    if (this._feedbackUpdateTimeout) {
      clearTimeout(this._feedbackUpdateTimeout);
      this._feedbackUpdateTimeout = null;
    }
    this._clearFeedbackCompletionTimer();
    this.closeFeedbackModalHost();
    this._removeVpeFeedbackModalListeners();
  }

  /** Stop MutationObserver on thread_messages_* and debounced mirror runs */
  _disconnectThreadMirror() {
    this._threadMessagesObserver?.disconnect();
    this._threadMessagesObserver = null;
    if (this._mirrorDebounceTimer) {
      clearTimeout(this._mirrorDebounceTimer);
      this._mirrorDebounceTimer = null;
    }
  }

  /** Clears STREAMING_DONE_MS post-content check */
  _clearFeedbackCompletionTimer() {
    if (this._feedbackCompletionTimer) {
      clearTimeout(this._feedbackCompletionTimer);
      this._feedbackCompletionTimer = null;
    }
  }

  /** Used by _fetchWithRetries to decide whether to retry the request */
  _retriableHttpStatus(status) {
    if (status === 0) return true;
    return (
      [408, 425, 429, 502, 503, 504].includes(status) || (status >= 500 && status <= 599)
    );
  }

  async _sleep(ms) {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Exponential backoff for fetches that may hit transient 5xx / network blips */
  async _fetchWithRetries(executeFetch) {
    let lastResponse;
    let lastError;
    for (let attempt = 0; attempt < HTTP_RETRY_MAX_ATTEMPTS; attempt += 1) {
      if (attempt > 0) await this._sleep(HTTP_RETRY_DELAYS_MS[attempt - 1]);
      try {
        const res = await executeFetch();
        lastResponse = res;
        if (res.ok) return res;
        if (!this._retriableHttpStatus(res.status) || attempt === HTTP_RETRY_MAX_ATTEMPTS - 1) {
          return res;
        }
      } catch (err) {
        lastError = err;
        if (attempt === HTTP_RETRY_MAX_ATTEMPTS - 1) throw err;
      }
    }
    if (lastError) throw lastError;
    return lastResponse;
  }

  /** getNewThread API with same retry policy as HTTP */
  async _getNewThreadWithRetries() {
    let lastError;
    for (let attempt = 0; attempt < HTTP_RETRY_MAX_ATTEMPTS; attempt += 1) {
      if (attempt > 0) await this._sleep(HTTP_RETRY_DELAYS_MS[attempt - 1]);
      try {
        return await getNewThread(this.assistantIdValue, this.userIdValue, 'vpe_feedback');
      } catch (err) {
        lastError = err;
        if (attempt === HTTP_RETRY_MAX_ATTEMPTS - 1) throw err;
      }
    }
    throw lastError;
  }

  /** Not enough chat history yet, or waiting on student to finish chat card */
  setFeedbackPlaceholder() {
    const responseContainer = this.responseContainerTarget;
    const continueButton = this.continueButtonTarget;
    if (responseContainer) {
      responseContainer.innerHTML =
        'If you do not see feedback below, close this card by clicking on the minus sign in the upper left-hand corner of the card, and make sure you asked at least five questions above. After submitting your questions, click "I don\'t have any more questions," rate the experience, and reopen this card.';
    }
    if (continueButton) {
      continueButton.disabled = true;
    }
  }

  /** Guarded entry: load VPE messages from DUS, then _generateFeedback if user message count sufficient */
  async fetchAndMaybeGenerateFeedback() {
    if (this._feedbackFetchInFlight) return;
    this._feedbackFetchInFlight = true;
    try {
      const dus = getDusFromDom();
      this.userSectionStateValue = dus;
      if (vpeChatErrorBlocksFeedback(dus)) {
        this.setChatErrorFeedbackMessage();
        return;
      }
      const messagesData = await this._fetchMessages();
      if (!this._hasEnoughUserMessages(messagesData)) {
        this.setFeedbackPlaceholder();
        return;
      }
      this.showLoading();
      await this._generateFeedback(messagesData);
    } catch (error) {
      console.error('AI Assistant Feedback Error:', error);
      this._showVpeFeedbackErrorModal();
    } finally {
      this._feedbackFetchInFlight = false;
    }
  }

  _feedbackArrayHasPersistedAssistant(messages) {
    if (!Array.isArray(messages)) return false;
    if (!this.vpeStructuredFeedbackValue) {
      return messages.some((m) => m?.content?.trim());
    }
    return messages.some(
      (m) =>
        m &&
        (Boolean(m.content && String(m.content).trim()) ||
          (m.id != null && String(m.id).trim() !== '')),
    );
  }

  _messageHasStructuredOutput(message) {
    const so = message?.structured_output;
    return Array.isArray(so) && so.length > 0;
  }

  _syncPayloadAssistantRow(messages) {
    if (!Array.isArray(messages)) return null;
    return [...messages].reverse().find((m) => {
      const r = m?.role;
      return r === 'assistant' || r === 1;
    });
  }

  /** Generating / Aquifer dots in responseContainer; card CONTINUE disabled until content or error */
  showLoading() {
    this.isLoading = true;
    this._vpeStructuredFeedbackRendered = false;
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.disabled = true;
    }
    if (this.hasLoadingTemplateTarget && this.hasResponseContainerTarget) {
      this.responseContainerTarget.replaceChildren(
        this.loadingTemplateTarget.content.cloneNode(true),
      );
      return;
    }
    return this._renderLoadingDots();
  }

  async _renderLoadingDots() {
    if (!this.hasResponseContainerTarget) return;
    try {
      const dotsHtml = await fetchAquiferLoadingDotsHtml();
      if (!this.isLoading || !this.hasResponseContainerTarget) return;
      this.responseContainerTarget.innerHTML = `
        <div class="flex justify-center items-center">
          ${dotsHtml}
        </div>
      `;
    } catch (error) {
      console.error('AI Assistant Feedback: failed to load Aquifer loading dots', error);
    }
  }

  /** Before error modal: stop timeouts, mirror, mark error, lock CONTINUE */
  _cleanupForFeedbackFatalError() {
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
      this.responseTimeout = null;
    }
    this._clearFeedbackCompletionTimer();
    this._feedbackStreamActive = false;
    if (this._activeFeedbackThreadId) {
      const cacheKey = this._feedbackGenerationCacheKey(this._activeFeedbackThreadId);
      if (cacheKey) feedbackGenerationInFlightCache.delete(cacheKey);
    }
    this._disconnectThreadMirror();
    this.isError = true;
    this.isLoading = false;
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.disabled = true;
    }
  }

  /** Same overlay copy as React AiAssistantFeedback; CONTINUE -> enableAiFallbackMode */
  async _showVpeFeedbackErrorModal() {
    this._cleanupForFeedbackFatalError();
    const fallbackModalRoot = this.hasErrorModalTarget ? this.errorModalTarget : null;
    const openEmbeddedModal = async () => openVpeFallbackErrorModal(this.application, fallbackModalRoot);

    if (fallbackModalRoot?.isConnected) {
      await waitForEmbeddedVpeErrorModalReady();
      if (await openEmbeddedModal()) {
        this.errorModalOpen = true;
        return true;
      }
    }

    const shown = await showAiVpeFeedbackErrorModal({
      documentId: this.documentIdValue,
      application: this.application,
      fallbackModalRoot,
    });
    this.errorModalOpen = shown;
    return shown;
  }

  /** Alias: RESPONSE_TIMEOUT_MS elapsed without usable assistant content */
  showError() {
    this._showVpeFeedbackErrorModal();
  }

  /** Remove .ai-error-modal, restore scroll, clear errorModalOpen / isError */
  hideErrorModal() {
    if (this.hasErrorModalTarget) {
      closeRailsuiModal(this.application, this.errorModalTarget);
    } else {
      removeDynamicVpeErrorModals();
    }
    this.errorModalOpen = false;
    this.isError = false;
  }

  continueFromErrorModal = async (event) => {
    event?.preventDefault?.();
    const result = await this.enableAiFallbackMode();
    if (result === false) this._reenableErrorModalContinueButtons();
    return result;
  };

  _reenableErrorModalContinueButtons() {
    const modalRoots = [
      this.hasErrorModalTarget ? this.errorModalTarget : null,
      ...document.querySelectorAll('[data-dynamic-vpe-error-modal]'),
    ].filter(Boolean);

    modalRoots.forEach((modalRoot) => {
      vpeErrorModalController(this.application, modalRoot)?.reenableContinue();
    });
  }

  /** DUS root aiAssistantFeedbackError + aiFallbackCards; navigate case without AI cards (handleAiAssistantFallback) */
  async enableAiFallbackMode() {
    const body = {
      document_user_state: {
        state: {
          root: {
            error: { value: 'aiAssistantFeedbackError', updatedAt: new Date().getTime() },
            aiFallbackCards: { value: true, updatedAt: new Date().getTime() },
          },
        },
      },
    };

    const response = await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body,
    });

    if (!response) {
      registerErrorInRollbar('VPE feedback error modal: fallback DUS patch failed', {
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        nodeUid: this.nodeUidValue,
      });
      this._reenableErrorModalContinueButtons();
      return false;
    }

    await handleAiAssistantFallback({
      currentSectionElement: currentSection(this.element),
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      application: this.application,
    });
    this.hideErrorModal();
    return true;
  }

  /** Create/use thread, mount turbo thread DOM, post composed chat as user message, observer streams reply */
  async _generateFeedback(messagesData) {
    this._feedbackStreamActive = true;
    try {
      const threadId = await this._getOrCreateThreadId();
      this._activeFeedbackThreadId = String(threadId);
      const cacheKey = this._feedbackGenerationCacheKey(this._activeFeedbackThreadId);
      if (cacheKey) feedbackGenerationInFlightCache.set(cacheKey, true);
      try {
        await this._saveThreadIdToState(threadId);
      } catch (e) {
        console.error('VPE feedback: saving threadId to document user state failed', e);
      }
      const tid = String(threadId);
      await this._ensureTurboMount(tid);
      this._connectThreadMessagesObserver(tid);
      const userMessage = this._composeUserMessage(messagesData);
      this.showLoading();
      this._setResponseTimeout();
      await this._postVpeFeedbackMessage(tid, userMessage.content);
    } catch (error) {
      this._feedbackStreamActive = false;
      if (this._activeFeedbackThreadId) {
        const cacheKey = this._feedbackGenerationCacheKey(this._activeFeedbackThreadId);
        if (cacheKey) feedbackGenerationInFlightCache.delete(cacheKey);
      }
      console.error('AI Assistant Feedback Error:', error);
      this._showVpeFeedbackErrorModal();
    }
  }

  /** Inject hidden thread_messages_* mount HTML if not already in DOM (for Action Cable / turbo updates) */
  async _ensureTurboMount(threadId) {
    const tid = String(threadId);
    if (document.getElementById(`thread_messages_${tid}`)) return;
    if (!this.hasTurboMountTarget) {
      throw new Error('Missing turbo mount target');
    }
    const res = await this._fetchWithRetries(() =>
      fetch(`/ai_assistant/messages/feedback_thread_mount?thread_id=${encodeURIComponent(tid)}`, {
        headers: {
          Accept: 'text/html',
          'X-CSRF-Token': getCsrfToken(),
        },
      }),
    );
    if (!res.ok) throw new Error('Failed to load feedback stream mount');
    this.turboMountTarget.innerHTML = await res.text();
  }

  /** Debounced mirror: assistant cells in thread -> responseContainer markdown */
  _connectThreadMessagesObserver(threadId) {
    const tid = String(threadId);
    const root = document.getElementById(`thread_messages_${tid}`);
    if (!root) return;
    this._threadMessagesObserver?.disconnect();
    const run = () => {
      if (this._mirrorDebounceTimer) clearTimeout(this._mirrorDebounceTimer);
      this._mirrorDebounceTimer = setTimeout(() => this._mirrorThreadMessagesToUI(tid), 80);
    };
    this._threadMessagesObserver = new MutationObserver(run);
    this._threadMessagesObserver.observe(root, { subtree: true, childList: true, characterData: true });
    run();
  }

  _assistantMessageBodyInThreadRoot(root) {
    const blocks = Array.from(root.querySelectorAll('[id$="_message"]'));
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const body = blocks[i].querySelector('.ai-message-body[data-ai-message-role="assistant"]');
      if (body) return body;
    }
    return null;
  }

  /**
   * Sync latest assistant message body into the visible feedback area.
   * Loading dots/empty -> keep loading; FEEDBACK_BAD_PHRASE -> fatal modal; else show markdown + arm completion timer.
   */
  _mirrorThreadMessagesToUI(threadId) {
    const root = document.getElementById(`thread_messages_${threadId}`);
    if (!root || !this.hasResponseContainerTarget) return;

    const assistantBody = this._assistantMessageBodyInThreadRoot(root);
    if (!assistantBody) return;

    if (assistantBody.querySelector('.aqui-loading-dots')) {
      this._clearFeedbackCompletionTimer();
      this.showLoading();
      this._latestFeedbackContent = '';
      return;
    }

    const text = assistantBody.textContent || '';

    if (text.includes(FEEDBACK_BAD_PHRASE)) {
      this._latestFeedbackContent = text;
      this._showVpeFeedbackErrorModal();
      return;
    }

    const structuredReady =
      this.vpeStructuredFeedbackValue &&
      assistantBody.querySelector('[data-vpe-structured-feedback-ready]');

    if (this.vpeStructuredFeedbackValue) {
      if (structuredReady) {
        if (this.responseTimeout) {
          clearTimeout(this.responseTimeout);
          this.responseTimeout = null;
        }

        this._latestFeedbackContent = text;
        this.isLoading = false;
        this.isError = false;
        this._vpeStructuredFeedbackRendered = true;
        this.responseContainerTarget.innerHTML = assistantBody.innerHTML;
        this._enableContinueButtonUnlessModalFeedbackSubmitted();

        if (this._feedbackUpdateTimeout) clearTimeout(this._feedbackUpdateTimeout);
        this._feedbackUpdateTimeout = setTimeout(() => {
          void this._persistFeedbackFromThread(threadId);
        }, 300);

        this._scheduleFeedbackCompletionTimer(STREAMING_DONE_MS);
        return;
      }

      if (!text.trim()) {
        this._clearFeedbackCompletionTimer();
        this.showLoading();
        this._latestFeedbackContent = '';
        this._vpeStructuredFeedbackRendered = false;
        return;
      }

      if (this.responseTimeout) {
        clearTimeout(this.responseTimeout);
        this.responseTimeout = null;
      }

      this._latestFeedbackContent = text;
      this.isLoading = false;
      this.isError = false;
      this._vpeStructuredFeedbackRendered = false;
      this.responseContainerTarget.innerHTML = convertSimpleMarkdown(text);
      this._enableContinueButtonUnlessModalFeedbackSubmitted();

      if (this._feedbackUpdateTimeout) clearTimeout(this._feedbackUpdateTimeout);
      this._feedbackUpdateTimeout = setTimeout(() => {
        void this._persistFeedbackFromThread(threadId);
      }, 300);

      this._scheduleFeedbackCompletionTimer(STREAMING_DONE_MS);
      return;
    }

    if (!text.trim()) {
      this._clearFeedbackCompletionTimer();
      this.showLoading();
      this._latestFeedbackContent = '';
      return;
    }

    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
      this.responseTimeout = null;
    }

    this._latestFeedbackContent = text;
    this.isLoading = false;
    this.isError = false;
    this.responseContainerTarget.innerHTML = convertSimpleMarkdown(text);
    this._enableContinueButtonUnlessModalFeedbackSubmitted();

    if (this._feedbackUpdateTimeout) clearTimeout(this._feedbackUpdateTimeout);
    this._feedbackUpdateTimeout = setTimeout(() => {
      void this._persistFeedbackFromThread(threadId);
    }, 300);

    this._scheduleFeedbackCompletionTimer(STREAMING_DONE_MS);
  }

  /** Best-effort: last assistant message from thread JSON into node feedback in DUS */
  async _persistFeedbackFromThread(threadId) {
    try {
      const res = await this._fetchWithRetries(() =>
        fetch(
          `/ai_assistant/messages/sync_from_thread?thread_id=${encodeURIComponent(String(threadId))}`,
          {
            headers: {
              Accept: 'application/json',
              'X-CSRF-Token': getCsrfToken(),
            },
          },
        ),
      );
      if (!res.ok) return;
      const data = await res.json();
      const msgs = Array.isArray(data.messages) ? data.messages : [];
      const lastAssistant = this._syncPayloadAssistantRow(msgs);
      if (!lastAssistant) return;

      let feedbackValue;
      if (this.vpeStructuredFeedbackValue) {
        if (this._messageHasStructuredOutput(lastAssistant)) {
          if (lastAssistant.id == null) return;
          feedbackValue = [
            {
              role: 'assistant',
              id: lastAssistant.id,
              content: lastAssistant.content || '',
            },
          ];
        } else if (lastAssistant.content?.trim()) {
          feedbackValue = [{ role: 'assistant', content: lastAssistant.content }];
        } else {
          return;
        }
      } else {
        if (!lastAssistant.content?.trim()) return;
        feedbackValue = [{ role: 'assistant', content: lastAssistant.content }];
      }

      const nodeStateKey = normalizeNodeUid(this.nodeUidValue);
      const currentFeedback = getDusEntryForUid(getDusFromDom(), this.nodeUidValue)?.feedback?.value;
      const feedbackChanged =
        JSON.stringify(currentFeedback ?? null) !== JSON.stringify(feedbackValue ?? null);
      if (!feedbackChanged) return;

      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        nodeUid: this.nodeUidValue,
        body: {
          document_user_state: {
            state: {
              [nodeStateKey]: {
                feedback: {
                  updatedAt: new Date().getTime(),
                  value: feedbackValue,
                },
              },
            },
          },
        },
      });
    } catch (error) {
      console.error('AI Assistant Feedback: failed to persist thread to DUS', error);
    }
  }

  /** POST user payload to feedback thread; turbo stream updates thread_messages_* */
  async _postVpeFeedbackMessage(threadId, content) {
    const token = getCsrfToken();
    const body = new URLSearchParams();
    body.append('authenticity_token', token);
    body.append('vpe_feedback', '1');
    body.append('ai_assistant_message[content]', content);
    const res = await this._fetchWithRetries(() =>
      fetch(`/ai_assistant/threads/${encodeURIComponent(String(threadId))}/messages`, {
        method: 'POST',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': token,
        },
        body,
      }),
    );
    if (!res.ok) throw new Error(`Feedback message request failed: ${res.status}`);
    const html = await res.text();
    if (window.Turbo?.renderStreamMessage && html?.trim()) {
      try {
        await Turbo.renderStreamMessage(html);
      } catch (e) {
        console.error('VPE feedback: Turbo.renderStreamMessage failed', e);
      }
    }
  }

  /**
   * After streaming finishes: if content still empty or bad phrase, fatal modal; else end _feedbackStreamActive.
   */
  _scheduleFeedbackCompletionTimer(delayMs) {
    this._clearFeedbackCompletionTimer();
    this._feedbackCompletionTimer = setTimeout(() => {
      this._feedbackCompletionTimer = null;
      const content = this._latestFeedbackContent || '';
      const structuredOk = this.vpeStructuredFeedbackValue && this._vpeStructuredFeedbackRendered;
      if (content.includes(FEEDBACK_BAD_PHRASE) || (!structuredOk && !content.trim())) {
        this._showVpeFeedbackErrorModal();
        return;
      }
      this._feedbackStreamActive = false;
      if (this._activeFeedbackThreadId) {
        const cacheKey = this._feedbackGenerationCacheKey(this._activeFeedbackThreadId);
        if (cacheKey) feedbackGenerationInFlightCache.delete(cacheKey);
      }
    }, delayMs);
  }

  /** Arm RESPONSE_TIMEOUT_MS -> showError (modal) if no successful mirror cleared it */
  _setResponseTimeout() {
    this.responseTimeout = setTimeout(() => {
      this.showError();
    }, RESPONSE_TIMEOUT_MS);
  }

  /** Student can proceed to rating modal once feedback HTML is shown */
  _enableContinueButtonUnlessModalFeedbackSubmitted() {
    if (!this.hasContinueButtonTarget) return;
    if (this.feedbackSubmittedValue) {
      this.continueButtonTarget.remove();
      return;
    }
    this.continueButtonTarget.disabled = false;
  }

  /** Hydrate from DUS on load / reconnect: show saved assistant feedback */
  displayFeedback(content) {
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
    }
    this.isLoading = false;
    this.isError = false;
    const row = Array.isArray(content) ? content[0] : content;

    if (this.vpeStructuredFeedbackValue && row?.id != null && String(row.id).trim() !== '') {
      void this._loadVpeStructuredFeedbackHtml(row.id, row?.content);
      return;
    }

    const feedback = Array.isArray(content) ? content[0]?.content || '' : content.content;

    if (!feedback || feedback.trim().length === 0) {
      this.showLoading();
      return;
    }

    const responseContainer = this.responseContainerTarget;
    if (responseContainer) {
      responseContainer.innerHTML = convertSimpleMarkdown(feedback);
    }

    this._enableContinueButtonUnlessModalFeedbackSubmitted();
  }

  /** Rating + submitFeedback; then section actions and continueToNextSection. */
  async _loadVpeStructuredFeedbackHtml(messageId, fallbackMarkdown) {
    try {
      const res = await this._fetchWithRetries(() =>
        fetch(
          `/ai_assistant/messages/vpe_feedback_html?message_id=${encodeURIComponent(String(messageId))}`,
          {
            headers: {
              Accept: 'text/html',
              'X-CSRF-Token': getCsrfToken(),
            },
          },
        ),
      );
      if (!res.ok) {
        throw new Error(`vpe_feedback_html failed: ${res.status}`);
      }
      const html = await res.text();
      if (!this.hasResponseContainerTarget) return;
      this.responseContainerTarget.innerHTML = html;
      this._vpeStructuredFeedbackRendered = true;
      this._latestFeedbackContent = this.responseContainerTarget.textContent || '';
      this._enableContinueButtonUnlessModalFeedbackSubmitted();
    } catch (error) {
      console.error('AI Assistant Feedback: structured hydrate failed', error);
      const fallback = fallbackMarkdown && String(fallbackMarkdown).trim();
      if (fallback && this.hasResponseContainerTarget) {
        this.responseContainerTarget.innerHTML = convertSimpleMarkdown(fallback);
        this._vpeStructuredFeedbackRendered = false;
        this._latestFeedbackContent = this.responseContainerTarget.textContent || '';
        this._enableContinueButtonUnlessModalFeedbackSubmitted();
        return;
      }
      this._showVpeFeedbackErrorModal();
    }
  }

  maybeDispatchRefreshContinueButtonsAfterFeedbackContinue() {
    if (railsStudentCaseUnlockContinue()) {
      dispatchCaseExperienceRefreshContinueButtonsIfTwoCol();
    }
  }

  _ensureVpeFeedbackModalListeners() {
    if (this._vpeFeedbackModalListenersBound) return;
    this._boundOnVpeFeedbackSubmitted = this.handleVpeFeedbackModalSubmitted.bind(this);
    this._boundOnVpeFeedbackClose = this.handleVpeFeedbackModalClose.bind(this);
    this.element.addEventListener('vpe-feedback-modal:submitted', this._boundOnVpeFeedbackSubmitted);
    this.element.addEventListener('vpe-feedback-modal:close', this._boundOnVpeFeedbackClose);
    this._vpeFeedbackModalListenersBound = true;
  }

  _removeVpeFeedbackModalListeners() {
    if (!this._vpeFeedbackModalListenersBound) return;
    this.element.removeEventListener('vpe-feedback-modal:submitted', this._boundOnVpeFeedbackSubmitted);
    this.element.removeEventListener('vpe-feedback-modal:close', this._boundOnVpeFeedbackClose);
    this._vpeFeedbackModalListenersBound = false;
  }

  buildVpeFeedbackModalUrl(threadId) {
    const params = new URLSearchParams({
      thread_id: String(threadId),
      context: 'vpe_feedback',
      node_uid: this.nodeUidValue,
    });
    return `/documents/${this.documentIdValue}/case_experience/vpe_feedback_modal?${params}`;
  }

  closeFeedbackModalHost() {
    if (this.hasFeedbackModalHostTarget) {
      this.feedbackModalHostTarget.style.display = 'none';
    }
    if (this.hasFeedbackModalTarget) {
      this.feedbackModalTarget.removeAttribute('src');
      this.feedbackModalTarget.innerHTML = '';
    }
    document.body.style.overflow = '';
  }

  feedbackModalFrameLoaded = (event) => {
    if (!this.hasFeedbackModalTarget || event.target !== this.feedbackModalTarget) return;
    if (!this.feedbackModalTarget.innerHTML.trim()) {
      this.closeFeedbackModalHost();
      this.feedbackSubmittedValue = true;
    }
  };

  feedbackModalFetchError = (event) => {
    if (!this.hasFeedbackModalTarget || event.target !== this.feedbackModalTarget) return;
    const status = event.detail?.response?.status;
    if (status === 204) {
      this.closeFeedbackModalHost();
      this.feedbackSubmittedValue = true;
      return;
    }
    this.closeFeedbackModalHost();
  };

  handleVpeFeedbackModalClose = () => {
    this.closeFeedbackModalHost();
  };

  handleVpeFeedbackModalSubmitted = async () => {
    this.closeFeedbackModalHost();
    if (this.feedbackSubmittedValue) return;
    const resolvedThreadId = this._resolvedThreadIdForVpeFeedbackSubmit();
    if (!resolvedThreadId) {
      registerErrorInRollbar('VPE feedback modal submit without resolvable thread id', {
        nodeUid: this.nodeUidValue,
      });
      return;
    }
    this.feedbackSubmittedValue = true;
    this.continueButtonTarget?.remove();
    const currentSectionElement = currentSection(this.element);
    const nextRelevantSection = getNextVisibleDocSection(currentSectionElement);
    const nextUid = nextRelevantSection?.dataset?.uid || null;
    if (nextUid) {
      const continueButtonUid = (() => {
        const btn = nextRelevantSection?.querySelector(
          '[data-case-experience--case-viewer-target="continueButton"]',
        );
        const uid = btn?.dataset?.uid;
        if (uid) return normalizeNodeUid(uid);
        return `continue_button_${normalizeNodeUid(nextUid)}`;
      })();
      const onClickActions = [
        {
          uid: generateUUID(),
          action: 'setNodeKVPair',
          arguments: {
            key: 'collapsed',
            uid: `${currentSectionElement.dataset.uid}`,
            value: true,
          },
        },
        {
          uid: generateUUID(),
          action: 'setNodeKVPair',
          arguments: { key: 'invisible', uid: `${nextUid}`, value: false },
        },
        {
          uid: generateUUID(),
          action: 'setNodeKVPair',
          arguments: { key: 'collapsed', uid: `${nextUid}`, value: false },
        },
        {
          uid: generateUUID(),
          action: 'setNodeKVPair',
          arguments: { key: 'invisible', uid: continueButtonUid, value: false },
        },
      ];
      await batchSetNodeKVPair(
        onClickActions,
        this.documentIdValue,
        this.userIdValue,
        this.application,
      );
    }
    await this.continueToNextSection();
    this.maybeDispatchRefreshContinueButtonsAfterFeedbackContinue();
  };

  /** Rating + submitFeedback; then section actions and linear continueToNextSection */
  openFeedbackModal() {
    if (!this.hasFeedbackModalTarget || this.feedbackSubmittedValue) return;
    const resolvedThreadId = this._resolvedThreadIdForVpeFeedbackSubmit();
    if (!resolvedThreadId) {
      registerErrorInRollbar('VPE feedback opened without resolvable thread id', {
        nodeUid: this.nodeUidValue,
      });
      return;
    }
    this._ensureVpeFeedbackModalListeners();
    if (this.hasFeedbackModalHostTarget) {
      this.feedbackModalHostTarget.style.display = '';
    }
    document.body.style.overflow = 'hidden';
    this.feedbackModalTarget.src = this.buildVpeFeedbackModalUrl(resolvedThreadId);
  }

  /** Shared case viewer navigation; unlock uses the same visible-card rules as React BlockNodes. */
  continueToNextSection = async () => {
    const useUnlockFlow = railsStudentCaseUnlockContinue();
    const rootSection =
      this.element.closest('[data-case-experience--case-viewer-target="section"]') ||
      currentSection(this.element);
    await continueToNextSection({
      element: this.element,
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      application: this.application,
      createContinueButtonIfNeeded: useUnlockFlow ? false : true,
      currentSectionUidOverride: rootSection?.dataset?.uid,
    });
  };

  /** VPE chat transcript for this case (by chatUid) from DUS-backed endpoint */
  async _fetchMessages() {
    const chatUid = encodeURIComponent(normalizeNodeUid(this.chatUidValue));
    const response = await this._fetchWithRetries(() =>
      fetch(
        `/ai_assistant/messages/get_messages_from_dus?user_id=${this.userIdValue}&document_id=${this.documentIdValue}&chat_uid=${chatUid}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
        },
      ),
    );
    if (!response.ok) throw new Error('Failed to fetch messages');
    return await response.json();
  }

  /** Same threshold as chat feedback button / React MINIMUM_MESSAGE_COUNT */
  _hasEnoughUserMessages(messagesData) {
    const userMessagesCount = Array.isArray(messagesData.messages)
      ? messagesData.messages.filter((msg) => msg.role === 'user').length
      : 0;
    return userMessagesCount >= AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT;
  }

  /** threadId value or new vpe_feedback thread via API */
  async _getOrCreateThreadId() {
    let threadId = this.threadIdValue;
    if (!threadId) {
      const thread = await this._getNewThreadWithRetries();
      threadId = thread.id;
      this.threadIdValue = threadId;
    }
    return threadId;
  }

  /** Persist feedback thread id on this feedback node */
  async _saveThreadIdToState(threadId) {
    await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      nodeUid: this.nodeUidValue,
      key: 'threadId',
      value: threadId,
    });
  }

  _threadIdFromTurboMountDom() {
    if (!this.hasTurboMountTarget) return '';
    const el = this.turboMountTarget.querySelector('[id^="thread_messages_"]');
    const id = el?.id;
    if (!id || !id.startsWith('thread_messages_')) return '';
    return id.slice('thread_messages_'.length);
  }

  _resolvedThreadIdForVpeFeedbackSubmit() {
    if (this.threadIdValue) return String(this.threadIdValue);
    if (this._activeFeedbackThreadId) return String(this._activeFeedbackThreadId);
    const fromDus = getDusFromDom()?.[this.nodeUidValue]?.threadId?.value;
    if (fromDus != null && String(fromDus).trim() !== '') return String(fromDus);
    return this._threadIdFromTurboMountDom();
  }

  _feedbackGenerationCacheKey(threadId) {
    if (!threadId || !this.nodeUidValue) return null;
    return `${normalizeNodeUid(this.nodeUidValue)}::${String(threadId)}`;
  }

  /** Single user-role prompt summarizing full thread for the feedback assistant */
  _composeUserMessage(messagesData) {
    return {
      content: messagesData.messages.map((msg) => `${msg.role}: ${msg.content}`).join('\n\n'),
      role: 'user',
    };
  }
}
